import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { useQuery } from '@tanstack/react-query';
import { useContext, useEffect, useState } from 'react';
import { DiefCfemContext } from '../../../../../contexts/DiefCfemContext';
import {
  ICodigoNcm as ICodigoNcmBaseCalculo,
  IRepasseParceiro,
  IVendaDireta,
} from '../../../../../interfaces/IBaseDeCalculo';
import { ICodigoNcm } from '../../../../../interfaces/ICodigoNcm';
import { IOperacao } from '../../../../../interfaces/IOperacaoPLG';
import { mesParaAbreviacao } from '../../../../../utils/mesParaAbreviacao';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import TitularArrendatarioOperacaoComponenteCalculo from './components/operacaoComponenteCalculo/operacaoComponenteCalculo';
import TitularArrendatarioOperacaoForm from './components/operacaoForm/titularArrendatarioOperacaoForm';
import styles from './titularArrendatarioNovaOperacao.module.scss';
import CustomStep from '../../customStep/customStep';
import ComponentesBaseDeCalculoFormItemNCM from './components/operacaoComponenteCalculo/components/baseDeCalculoFormItemNCM/baseDeCalculoFormItemNCM';
import TitularArrendatarioOperacaoFormReadOnly from './components/operacaoFormReadOnly/titularArrendatarioOperacaoFormReadOnly';
import { useNotificacao } from '../../../../../hooks/useNotificacao';
import { IMunicipio } from '../../../../../interfaces/IProcessos';
import { ETipoOperacao, OperacaoDetalhe } from '../../../../../enums/ETipoOperacao';
import { Axios, AxiosError } from 'axios';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface ITitularArrendatarioNovaArrematacaoProps {
  setNovaOperacaoPLG: React.Dispatch<React.SetStateAction<boolean>>;
  edicaoOperacao: IOperacao | undefined;
  setEdicaoOperacao: React.Dispatch<React.SetStateAction<IOperacao | undefined>>;
  salvarRascunho: () => void;
}

export interface ITitularArrendatarioNovaOperacaoForm {
  numeroProcesso: string;
  substancia: string;
  municipio: IMunicipio;
  unidadeMedida: string;
  tipoVinculo: 'TITULAR' | 'ARRENDATARIO' | '';
  hasProducao: boolean;
  quantidadeProduzida?: number;
  hasFatoGerador: boolean;
  tipoOperacao: 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE' | 'REPASSE_PARCEIRO' | 'SEM_OPERACAO';
  isRejeito: boolean;
  cadeiaProdutiva: string;
  observacoes: string;
}

function TitularArrendatarioNovaOperacaoPLG({
  setNovaOperacaoPLG,
  edicaoOperacao,
  setEdicaoOperacao,
}: Readonly<ITitularArrendatarioNovaArrematacaoProps>) {
  const [codigosNCM, setCodigosNCM] = useState<ICodigoNcm[]>([]);
  const [listaCodigosNCM, setListaCodigosNCM] = useState<ICodigoNcmBaseCalculo[]>([]);
  const context = useContext(DiefCfemContext);
  const declaracaoTitularArrendatario = context?.declaracaoTitularArrendatario;
  const [isFormularioValido, setIsFormularioValido] = useState(false);
  const [mensagemErro, setMensagemErro] = useState<string[]>(['']);

  const [tipoOperacao, setTipoOperacao] = useState<
    'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE' | 'REPASSE_PARCEIRO' | 'SEM_OPERACAO'
  >('VENDA_DIRETA_PRIMEIRO_ADQUIRENTE');
  const [vendasDiretas, setVendasDiretas] = useState<IVendaDireta[]>([]);
  const [repassesParceiros, setRepassesParceiros] = useState<IRepasseParceiro[]>([]);

  const [vendaDireta, setVendaDireta] = useState<IVendaDireta>();
  const [repasseParceiro, setRepasseParceiro] = useState<IRepasseParceiro>();
  const [enableNCM, setEnableNCM] = useState(false);
  const [enableBaseCalculo, setEnableBaseCalculo] = useState(false);
  const [enableConcluirOperacao, setEnableConcluirOperacao] = useState(false);
  const [unidadesMedida, setUnidadesMedida] = useState<{ label: string; value: string }[]>([]);

  const {
    getCodigosNCM,
    postCadastrarOperacaoPLGItem,
    postCadastrarVendaDireta,
    postCadastrarRepasseParceiro,
    getOperacaoPLGById,
    putEditarRepasseParceiro,
    putEditarVendaDireta,
    putEditarOperacaoPLGItem,
  } = useBackEndRequests();

  const [stepFormulario, setStepFormulario] = useState(1);
  const [operacao, setOperacao] = useState<IOperacao>();
  const mostrarNotificacao = useNotificacao();
  const [isEdicaoBaseCalculo, setIsEdicaoBaseCalculo] = useState(false);
  const [repasseParceiroEdicao, setRepasseParceiroEdicao] = useState<IRepasseParceiro | undefined>();
  const [vendaDiretaEdicao, setVendaDiretaEdicao] = useState<IVendaDireta | undefined>();
  const [isEdicaoCabecalho, setIsEdicaoCabecalho] = useState(false);
  const [isEdicaoLoading, setIsEdicaoLoading] = useState(false);

  const { data: codigosNCMData, isLoading } = useQuery<ICodigoNcm[], IErrorWithSentryId>({
    queryKey: ['codigosNCMQuery'],
    queryFn: () => getCodigosNCM(),
    refetchOnWindowFocus: false,
  });

  const {
    data: operacaoDataGet,
    isLoading: isOperacaoDataGetLoading,
    refetch: refetchGetOperacaoData,
  } = useQuery<IOperacao, IErrorWithSentryId>({
    queryKey: ['getOperacaoPLG', edicaoOperacao?.id || operacao?.id || 0],
    queryFn: async () => {
      return await getOperacaoPLGById(edicaoOperacao?.id || operacao?.id || 0);
    },
    enabled: false,
  });

  const {
    data: operacaoDataPost,
    isLoading: isOperacaoDataLoadingPost,
    refetch: refetchPostPutCadastrarOperacaoPLGItem,
    error: erroOperacaoDataPost,
  } = useQuery<IOperacao, IErrorWithSentryId>({
    queryKey: ['postCadastrarOperacaoPLGItem'],
    queryFn: async () => {
      if (operacao && operacao.id === 0) {
        return await postCadastrarOperacaoPLGItem(operacao!);
      } else if (operacao && operacao.id !== 0) {
        return await putEditarOperacaoPLGItem(operacao);
      }
      throw erroOperacaoDataPost;
    },
    enabled: false,
    staleTime: 0,
    retry: false,
  });

  const {
    data: vendaDiretaData,
    isLoading: vendaDiretaDataLoading,
    error: erroVendaDiretaData,
    refetch: refetchPostPutVendaDiretaData,
  } = useQuery<IVendaDireta, IErrorWithSentryId>({
    queryKey: ['postPutCadastrarVendaDireta'],
    queryFn: async () => {
      if (vendaDireta && vendaDireta.id === 0) {
        return await postCadastrarVendaDireta(vendaDireta);
      } else if (vendaDireta && vendaDireta.id !== 0) {
        return await putEditarVendaDireta(vendaDireta);
      }
      throw erroVendaDiretaData;
    },
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime: 0,
    retry: false,
  });

  const {
    data: repasseParceiroData,
    isLoading: repasseParceiroDataLoading,
    error: erroRepasseParceiroData,
    refetch: refetchPostPutRepasseParceiroData,
  } = useQuery<IRepasseParceiro, IErrorWithSentryId>({
    queryKey: ['postPutCadastrarRepasseParceiro'],
    queryFn: async () => {
      if (repasseParceiro && repasseParceiro.id === 0) {
        return await postCadastrarRepasseParceiro(repasseParceiro);
      } else if (repasseParceiro && repasseParceiro.id !== 0) {
        return await putEditarRepasseParceiro(repasseParceiro);
      }
      throw new Error('Não foi possível cadastrar o repasse parceiro.' + erroRepasseParceiroData);
    },
    refetchOnWindowFocus: false,
    enabled: false,
    retry: false,
  });

  const isOperacaoHabilitadaParaFinalizar = (operacao: IOperacao): boolean => {
    if (operacao.tipoOperacao === 'SEM_OPERACAO') {
      return true;
    }
    if (operacao.tipoOperacao === 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE') {
      if (!operacao.vendasDiretas) {
        return false;
      }
      if (operacao.vendasDiretas.length === 0) {
        return false;
      }
      return operacao.vendasDiretas?.every((venda) => venda.codigosNCM.length > 0);
    }
    if (operacao.tipoOperacao === 'REPASSE_PARCEIRO') {
      if (!operacao.repasses) {
        return false;
      }
      if (operacao.repasses.length === 0) {
        return false;
      }
      return operacao.repasses?.every((repasse) => repasse.codigosNCM.length > 0);
    }
    return false;
  };

  useEffect(() => {
    const fetchOperacaoData = async () => {
      if (edicaoOperacao) {
        setIsEdicaoLoading(true);
        const { data } = await refetchGetOperacaoData();
        if (data) {
          setEdicaoOperacao(undefined);
          setEnableBaseCalculo(true);
          setStepFormulario(2);
          setTipoOperacao(data.tipoOperacao);
          setEnableConcluirOperacao(isOperacaoHabilitadaParaFinalizar(data));
          setVendaDireta(undefined);
          setRepasseParceiro(undefined);
        }
        setIsEdicaoLoading(false);
      }
    };

    fetchOperacaoData();
  }, [edicaoOperacao]);

  useEffect(() => {
    if (codigosNCMData) {
      setCodigosNCM(codigosNCMData);
    }
  }, [codigosNCMData]);

  const salvarOperacao = async () => {
    if (!isFormularioValido) {
      mensagemErro.forEach((erro) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: erro,
        });
      });
      return;
    }

    const retornoPostPutOperacao = await refetchPostPutCadastrarOperacaoPLGItem();
    if (retornoPostPutOperacao.error) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Não foi possível salvar a operação.',
        complementoSentryId: retornoPostPutOperacao.error,
      });
      return;
    }

    if (operacao?.tipoOperacao === 'SEM_OPERACAO') {
      concluirAdicao();
      return;
    }

    setStepFormulario(2);
    setEnableBaseCalculo(true);
  };

  useEffect(() => {
    if (operacaoDataPost?.id) {
      setOperacao(operacaoDataPost);
      setVendasDiretas(operacaoDataPost.vendasDiretas || []);
      setRepassesParceiros(operacaoDataPost.repasses || []);
      setVendaDireta(undefined);
      setRepasseParceiro(undefined);
    }
  }, [operacaoDataPost?.id]);

  useEffect(() => {
    if (vendaDiretaData?.id) {
      setVendaDireta(vendaDiretaData);
      refetchGetOperacaoData();
    }
  }, [vendaDiretaData?.id]);

  useEffect(() => {
    if (repasseParceiroData?.id) {
      setRepasseParceiro(repasseParceiroData);
      refetchGetOperacaoData();
    }
  }, [repasseParceiroData?.id]);

  useEffect(() => {
    if (operacaoDataGet) {
      setOperacao(operacaoDataGet);
      if (operacaoDataGet.vendasDiretas) setVendasDiretas(operacaoDataGet.vendasDiretas || []);
      if (operacaoDataGet.repasses) setRepassesParceiros(operacaoDataGet.repasses);
    }
  }, [operacaoDataGet]);

  const salvarComponenteDeBC = async () => {
    if (!isFormularioValido) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Formulário inválido (' + mensagemErro + ')',
      });
      return;
    }

    if (vendaDireta) {
      if (
        vendasDiretas.length > 0 &&
        vendasDiretas.filter(
          (venda) => venda.cpfCnpj === vendaDireta.cpfCnpj && venda.id !== vendaDireta.id && venda.cpfCnpj !== null
        ).length > 0
      ) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Já há uma Venda Direta cadastrada para o CPF/CNPJ informado',
        });
        return;
      }
      vendaDireta.idDeclaracaoItem = operacao?.id || 0;
      vendaDireta.id = vendaDireta.id || 0;

      const retornoPostPutVendaDireta = await refetchPostPutVendaDiretaData();
      if (retornoPostPutVendaDireta.error) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível salvar a Venda Direta.',
          complementoSentryId: retornoPostPutVendaDireta.error,
        });
        return;
      } else {
        setListaCodigosNCM(retornoPostPutVendaDireta.data?.codigosNCM || []);
      }
    }

    if (repasseParceiro) {
      if (
        repassesParceiros.length > 0 &&
        repassesParceiros.filter(
          (repasse) => repasse.cpfCnpj === repasseParceiro.cpfCnpj && repasse.id !== repasseParceiro.id
        ).length > 0
      ) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Já há um Repasse cadastrado para o CPF/CNPJ informado',
        });
        return;
      }
      repasseParceiro.idDeclaracaoItem = operacao?.id || 0;
      repasseParceiro.id = repasseParceiro.id || 0;

      const retornoPostPutRepasse = await refetchPostPutRepasseParceiroData();
      if (retornoPostPutRepasse.error) {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Não foi possível salvar o Repasse.',
          complementoSentryId: retornoPostPutRepasse.error,
        });
        return;
      } else {
        setListaCodigosNCM(retornoPostPutRepasse.data?.codigosNCM || []);
      }
    }

    if (isEdicaoBaseCalculo) {
      setEnableBaseCalculo(false);
      setEnableConcluirOperacao(true);
      setEnableNCM(true);
    } else {
      setEnableBaseCalculo(false);
      setEnableConcluirOperacao(false);
      setEnableNCM(true);
    }
  };

  const salvarNCMs = () => {
    if (listaCodigosNCM.length === 0 && !isEdicaoBaseCalculo) {
      mostrarNotificacao({
        tipo: 'danger',
        conteudo: 'Adicione ao menos um NCM',
      });
      return;
    }

    if (vendaDireta) {
      vendaDireta.codigosNCM = listaCodigosNCM;
      setListaCodigosNCM([]);
      setVendaDireta(undefined);
      setVendaDiretaEdicao(undefined);
    }

    if (repasseParceiro) {
      repasseParceiro.codigosNCM = listaCodigosNCM;
      setListaCodigosNCM([]);
      setRepasseParceiro(undefined);
      setRepasseParceiroEdicao(undefined);
    }

    setEnableNCM(false);
    setEnableBaseCalculo(true);
    setEnableConcluirOperacao(true);
    setIsEdicaoBaseCalculo(false);
  };

  const concluirAdicao = () => {
    if (declaracaoTitularArrendatario) {
      if (declaracaoTitularArrendatario.operacoes) {
        declaracaoTitularArrendatario.operacoes.push(operacao!);
      } else {
        declaracaoTitularArrendatario.operacoes = [operacao!];
      }

      context?.setDeclaracaoTitularArrendatario(declaracaoTitularArrendatario);
    } else {
      alert('declaracaoTitularArrendatario nulo');
    }

    window.scrollTo(0, 0);
    setNovaOperacaoPLG(false);
  };

  return (
    <>
      <div className={styles.containerPrincipal}>
        <div className={styles.stepFormularioContainer}>
          <CustomStep
            stepAtual={stepFormulario}
            setStepFormulario={setStepFormulario}
            steps={[{ label: 'Dados da Operação' }, { label: 'Formação da Base de Cálculo da CFEM' }]}
          />
        </div>
        <div className={styles.formularioContainer}>
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              <h4 className={styles.titleText}>Processos Minerários</h4>
              <span className={styles.subtitleText}>
                {edicaoOperacao ? 'Editar Operação' : 'Nova Operação'} - Permissão de Lavra Garimpeira
              </span>
            </div>
            <span className={styles.mesAno}>
              {mesParaAbreviacao(parseInt(sessionStorage.getItem('mesDeclaracao') ?? '-1'))}{' '}
              {sessionStorage.getItem('anoDeclaracao')}
            </span>
          </div>
          <div className={styles.formProcessoContainer}>
            {stepFormulario === 1 ? (
              isEdicaoLoading ? (
                <BrLoading large label='Carregando...' />
              ) : (
                <TitularArrendatarioOperacaoForm
                  tipoOperacao={tipoOperacao}
                  setTipoOperacao={setTipoOperacao}
                  setStepFormulario={setStepFormulario}
                  setOperacao={setOperacao}
                  setIsFormularioValido={setIsFormularioValido}
                  setMensagemErro={setMensagemErro}
                  operacao={operacao}
                  setUnidadesMedida={setUnidadesMedida}
                  unidadesMedida={unidadesMedida}
                  isEdicaoCabecalho={isEdicaoCabecalho}
                />
              )
            ) : isOperacaoDataGetLoading ? (
              <BrLoading large label='Carregando...' />
            ) : (
              <TitularArrendatarioOperacaoFormReadOnly
                operacao={operacao}
                setStepFormulario={setStepFormulario}
                setIsEdicaoCabecalho={setIsEdicaoCabecalho}
                setUnidadesMedida={setUnidadesMedida}
                unidadesMedida={unidadesMedida}
              />
            )}
            {isLoading || isOperacaoDataLoadingPost ? (
              <BrLoading large label='Carregando...' />
            ) : (
              stepFormulario >= 2 &&
              operacao?.tipoOperacao &&
              operacao.tipoOperacao !== ETipoOperacao[ETipoOperacao.SEM_OPERACAO] && (
                <div className={styles.componentesBaseCalculoContainer}>
                  <div className={styles.componentesTitle}>
                    {OperacaoDetalhe[ETipoOperacao[operacao?.tipoOperacao || 'VENDA_DIRETA_PRIMEIRO_ADQUIRENTE']]
                      ?.label || '-'}
                  </div>
                  <div className={styles.baseDeCalculoContainer}>
                    <TitularArrendatarioOperacaoComponenteCalculo
                      tipoOperacao={tipoOperacao}
                      vendasDiretas={vendasDiretas}
                      setVendaDireta={setVendaDireta}
                      setVendasDiretas={setVendasDiretas}
                      repassesParceiros={repassesParceiros}
                      setRepassesParceiros={setRepassesParceiros}
                      setRepasseParceiro={setRepasseParceiro}
                      setIsFormularioValido={setIsFormularioValido}
                      stepFormulario={stepFormulario}
                      setMensagemErro={setMensagemErro}
                      enableBaseCalculo={enableBaseCalculo}
                      vendaDireta={vendaDireta}
                      repasseParceiro={repasseParceiro}
                      setIsEdicaoBaseCalculo={setIsEdicaoBaseCalculo}
                      setVendaDiretaEdicao={setVendaDiretaEdicao}
                      setRepasseParceiroEdicao={setRepasseParceiroEdicao}
                      vendaDiretaEdicao={vendaDiretaEdicao}
                      repasseParceiroEdicao={repasseParceiroEdicao}
                    />
                    {!enableNCM && (
                      <div className={styles.botaoContainer}>
                        {isEdicaoBaseCalculo ? (
                          <BrButton secondary onClick={() => salvarComponenteDeBC()}>
                            <i className={`fas fa-check fa-md  ${styles.columnIcon}`} />
                            <span>Editar dados e avançar para NCMs</span>
                          </BrButton>
                        ) : (
                          <BrButton secondary onClick={() => salvarComponenteDeBC()}>
                            <i className={`fas fa-plus fa-md  ${styles.columnIcon}`} />
                            {operacao?.tipoOperacao ===
                            ETipoOperacao[ETipoOperacao.VENDA_DIRETA_PRIMEIRO_ADQUIRENTE] ? (
                              <span>Incluir Primeiro Adquirente</span>
                            ) : (
                              <span>Incluir Participante</span>
                            )}
                          </BrButton>
                        )}
                      </div>
                    )}
                    {vendaDiretaDataLoading || repasseParceiroDataLoading ? (
                      <BrLoading large label='Carregando...' />
                    ) : (
                      <div>
                        <div className={styles.componenteNCM}>
                          <ComponentesBaseDeCalculoFormItemNCM
                            idVendaDireta={vendaDireta?.id}
                            idRepasseParceiro={repasseParceiro?.id}
                            listaCodigosNCM={listaCodigosNCM}
                            setListaCodigosNCM={setListaCodigosNCM}
                            codigosNCM={codigosNCM}
                            type='Operação'
                            isReadOnly={!enableNCM}
                            unidadesMedida={unidadesMedida}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
      {stepFormulario === 1 && operacao?.tipoOperacao !== 'SEM_OPERACAO' && (
        <div className={styles.botaoContainer}>
          <BrButton
            secondary
            onClick={() => {
              window.scrollTo(0, 0);
              setNovaOperacaoPLG(false);
            }}
          >
            Desistir <i className={`fas fa-times fa-md`} />
          </BrButton>
          <BrButton primary onClick={() => salvarOperacao()}>
            Salvar e avançar <i className={`fas fa-check fa-md`} />
          </BrButton>
        </div>
      )}
      {stepFormulario === 1 && operacao?.tipoOperacao === 'SEM_OPERACAO' && (
        <div className={styles.botaoContainer}>
          <BrButton
            secondary
            onClick={() => {
              window.scrollTo(0, 0);
              setNovaOperacaoPLG(false);
            }}
          >
            Desistir <i className={`fas fa-times fa-md`} />
          </BrButton>
          <BrButton primary onClick={() => salvarOperacao()}>
            Finalizar <i className={`fas fa-check fa-md`} />
          </BrButton>
        </div>
      )}
      {stepFormulario === 2 &&
        enableBaseCalculo &&
        (!enableConcluirOperacao || vendaDireta || repasseParceiro) &&
        !enableNCM && (
          <div className={styles.botaoContainer}>
            <BrButton
              secondary
              onClick={() => {
                window.scrollTo(0, 0);
                setEdicaoOperacao(undefined);
                setNovaOperacaoPLG(false);
              }}
            >
              <i className={`fas fa-arrow-left fa-md`} /> Voltar
            </BrButton>
            <BrButton primary onClick={() => salvarComponenteDeBC()}>
              Salvar e avançar <i className={`fas fa-check fa-md`} />
            </BrButton>
          </div>
        )}
      {stepFormulario === 2 && enableNCM && (
        <div className={styles.botaoContainer}>
          <BrButton
            secondary
            onClick={() => {
              window.scrollTo(0, 0);
              setNovaOperacaoPLG(false);
            }}
          >
            <i className={`fas fa-arrow-left fa-md`} /> Voltar
          </BrButton>
          <BrButton primary onClick={() => salvarNCMs()}>
            Salvar <i className={`fas fa-check fa-md`} />
          </BrButton>
        </div>
      )}
      {stepFormulario === 2 && enableBaseCalculo && enableConcluirOperacao && !vendaDireta && !repasseParceiro && (
        <div className={styles.botaoContainer}>
          <BrButton
            secondary
            onClick={() => {
              window.scrollTo(0, 0);
              setNovaOperacaoPLG(false);
            }}
          >
            <i className={`fas fa-arrow-left fa-md`} /> Voltar
          </BrButton>
          <BrButton primary onClick={() => concluirAdicao()}>
            Finalizar <i className={`fas fa-check fa-md`} />
          </BrButton>
        </div>
      )}
    </>
  );
}

export default TitularArrendatarioNovaOperacaoPLG;
